import { Directive, ElementRef, forwardRef, HostListener, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ShDatepickerElement } from '../types/sh-datepicker-element.type';
import { formatShDatepickerValue } from '../utils/sh-datepicker.utils';

@Directive({
    selector: 'sh-datepicker[formControlName], sh-datepicker[formControl], sh-datepicker[ngModel]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShDatepickerValueAccessorDirective),
            multi: true
        }
    ]
})
export class ShDatepickerValueAccessorDirective implements ControlValueAccessor {
    private readonly elementRef = inject<ElementRef<ShDatepickerElement>>(ElementRef);

    private onChange: (value: Date | null) => void = () => undefined;
    private onTouched: () => void = () => undefined;
    private value: Date | null = null;
    private hasScheduledValueWrite = false;

    public writeValue(value: Date | null | undefined): void {
        this.value = value ?? null;
        if (this.hasScheduledValueWrite) {
            return;
        }

        this.hasScheduledValueWrite = true;
        queueMicrotask(() => {
            this.hasScheduledValueWrite = false;
            this.elementRef.nativeElement.value = this.value
                ? formatShDatepickerValue(this.value, this.elementRef.nativeElement)
                : '';
        });
    }

    public registerOnChange(fn: (value: Date | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.elementRef.nativeElement.disabled = isDisabled;
    }

    @HostListener('value-changed')
    protected handleValueChanged(): void {
        this.onChange(this.elementRef.nativeElement.standardValue?.[0] ?? null);
    }

    @HostListener('blur')
    protected handleBlur(): void {
        this.onTouched();
    }
}
